import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Programs', path: '/programs' },
    { name: 'Clubs & Services', path: '/clubs' },
    { name: 'Big Ike Revival', path: '/big-ike-revival' },
    { name: 'Partners', path: '/partners' },
    { name: 'Grow With ME', path: '/book' },
    { name: 'Events', path: '/events' },
    { name: 'Get Involved', path: '/get-involved' }
  ];

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) return;
    fetch(`${API}/api/auth/google/me`, { credentials: 'include' })
      .then(r => { if (r.ok) return r.json(); throw new Error(); })
      .then(user => setGoogleUser(user))
      .catch(() => setGoogleUser(null));
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    await fetch(`${API}/api/auth/google/logout`, { method: 'POST', credentials: 'include' });
    setGoogleUser(null);
    setShowUserMenu(false);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="https://customer-assets.emergentagent.com/job_why-me-org/artifacts/qhc2a24j_WHYME%20LOGO.jpg"
              alt="WHY ME Foundation"
              className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  isActive(link.path)
                    ? 'text-[#E6B933] bg-[#E6B933]/10'
                    : 'text-gray-700 hover:text-[#E6B933] hover:bg-gray-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/get-involved">
              <Button className="ml-2 bg-[#E6B933] hover:bg-[#D4A01F] text-white font-semibold px-5 transition-all duration-300 hover:scale-105" data-testid="nav-donate-btn">
                Donate Now
              </Button>
            </Link>

            {/* Google User / Sign In */}
            {googleUser ? (
              <div className="relative ml-2" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 hover:border-[#E6B933]/50 transition-all"
                  data-testid="user-menu-btn"
                >
                  {googleUser.picture ? (
                    <img src={googleUser.picture} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <User className="w-5 h-5 text-gray-600" />
                  )}
                  <span className="text-sm font-medium text-gray-700 max-w-[100px] truncate">{googleUser.name?.split(' ')[0]}</span>
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 truncate">{googleUser.name}</p>
                      <p className="text-xs text-gray-500 truncate">{googleUser.email}</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                      data-testid="google-logout-btn"
                    >
                      <LogOut className="w-4 h-4" /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleGoogleLogin}
                className="ml-2 flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 hover:border-[#E6B933]/50 hover:bg-gray-50 transition-all text-sm font-medium text-gray-700"
                data-testid="nav-sign-in-btn"
              >
                <User className="w-4 h-4" /> Sign In
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-300"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden pb-6 space-y-2 animate-in slide-in-from-top duration-300">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isActive(link.path)
                    ? 'text-[#E6B933] bg-[#E6B933]/10'
                    : 'text-gray-700 hover:text-[#E6B933] hover:bg-gray-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            {googleUser ? (
              <div className="px-4 py-3 border-t border-gray-100 mt-2 pt-4">
                <div className="flex items-center gap-3 mb-3">
                  {googleUser.picture && <img src={googleUser.picture} alt="" className="w-8 h-8 rounded-full" />}
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{googleUser.name}</p>
                    <p className="text-xs text-gray-500">{googleUser.email}</p>
                  </div>
                </div>
                <button onClick={() => { handleLogout(); setIsOpen(false); }} className="text-sm text-red-600 flex items-center gap-2">
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              </div>
            ) : (
              <button
                onClick={() => { handleGoogleLogin(); setIsOpen(false); }}
                className="block w-full text-left px-4 py-3 rounded-lg font-medium text-gray-700 hover:text-[#E6B933] hover:bg-gray-50"
              >
                Sign In with Google
              </button>
            )}
            <Link to="/get-involved" onClick={() => setIsOpen(false)}>
              <Button className="w-full mt-2 bg-[#E6B933] hover:bg-[#D4A01F] text-white font-semibold">
                Donate Now
              </Button>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
